/**
 * Private Suite Constants and Configuration
 * Shared data for all Private Suite modes
 */

import { 
  Zap, Moon, Sun, Target, Heart, Eye, Sparkles, Shield,
  Dumbbell, Trophy, Wind, Swords, Leaf, Flag
} from "lucide-react";

// Emotional states with icons and colors
export const CURRENT_STATES = [
  { id: "stressed", label: "Stressed", icon: Zap, color: "from-orange-400 to-red-400" },
  { id: "low_energy", label: "Low Energy", icon: Moon, color: "from-indigo-400 to-purple-400" },
  { id: "distracted", label: "Distracted", icon: Target, color: "from-yellow-400 to-orange-400" },
  { id: "anxious", label: "Anxious", icon: Heart, color: "from-pink-400 to-rose-400" },
  { id: "unfocused", label: "Unfocused", icon: Eye, color: "from-blue-400 to-indigo-400" },
  { id: "tired", label: "Tired", icon: Moon, color: "from-slate-400 to-gray-400" },
];

// Desired states for Meditation mode
export const DESIRED_STATES = [
  { id: "calm", label: "Calm", icon: Heart, color: "from-emerald-400 to-teal-400" },
  { id: "focus", label: "Focus", icon: Target, color: "from-blue-400 to-cyan-400" },
  { id: "confidence", label: "Confidence", icon: Zap, color: "from-amber-400 to-orange-400" },
  { id: "energy", label: "Energy", icon: Sun, color: "from-yellow-400 to-amber-400" },
  { id: "presence", label: "Presence", icon: Eye, color: "from-violet-400 to-purple-400" },
  { id: "clarity", label: "Clarity", icon: Sparkles, color: "from-cyan-400 to-blue-400" },
  { id: "sleep", label: "Sleep", icon: Moon, color: "from-indigo-400 to-purple-400" },
  { id: "core_confidence", label: "Core Confidence", icon: Shield, color: "from-amber-400 to-orange-400" },
];

// Duration options. `custom` flips a numeric input so the user can
// dial in any 1-60 min length (committed by storing the chosen
// seconds value into the same `duration` slot).
export const DURATIONS = [
  { id: "3min", label: "3 minutes", seconds: 180 },
  { id: "5min", label: "5 minutes", seconds: 300 },
  { id: "10min", label: "10 minutes", seconds: 600 },
  { id: "20min", label: "20 minutes", seconds: 1200 },
  { id: "custom", label: "Custom length", seconds: null, custom: true, description: "Choose any 1–60 minutes" },
  { id: "continuous", label: "Continuous", seconds: null, description: "Loop until you stop" },
];

// ── Music styles ─────────────────────────────────────────────────
// Curated for Reclaim's calm-performance / subconscious-installation
// tone. Research-grounded: removed off-genre picks (Hip-Hop, EDM,
// Deep House, Motivation, Lullaby) that clashed with the premium
// meditative aesthetic and replaced them with genres shown to
// support state shifts:
//
//   • Activation  → "trailer calm" / slow-build orchestral that lifts
//                   state without aggression.
//   • Focus       → lo-fi, deep minimal, and warm drone (no lyrics)
//                   known to extend flow state.
//   • Calm        → neoclassical piano, solfeggio, warm pad — the
//                   evergreen meditation base.
//   • Nature      → forest, rain, ocean — strongest para-sympathetic
//                   trigger per sleep research.
//   • Sleep       → soft piano nocturne, pink noise, deep drone,
//                   binaural-friendly low frequencies.
export const MUSIC_STYLES = [
  // Activation — warm lift without aggression
  { id: "cinematic",    label: "Cinematic",     description: "Slow-build, epic calm",       category: "activation" },
  { id: "trailer_calm", label: "Hero",          description: "Warm strings & rising pulse", category: "activation" },
  { id: "tribal",       label: "Tribal Drums",  description: "Grounded, primal focus",      category: "activation" },
  { id: "arrival",      label: "Arrival",       description: "Minimalist tension",          category: "activation" },

  // Focus — sustain flow state (all instrumental)
  { id: "lofi",         label: "Lo-Fi",         description: "Mellow study beats",          category: "focus" },
  { id: "deep_minimal", label: "Deep Minimal",  description: "Distraction-free loop",       category: "focus" },
  { id: "warm_drone",   label: "Warm Drone",    description: "Soft harmonic bed",           category: "focus" },
  { id: "flow_pulse",   label: "Flow Pulse",    description: "Steady 60 BPM lift",          category: "focus" },

  // Calm — meditation & reset
  { id: "ambient",      label: "Ambient",       description: "Atmospheric air",             category: "calm" },
  { id: "neoclassical", label: "Neoclassical",  description: "Solo piano, strings",         category: "calm" },
  { id: "solfeggio",    label: "Solfeggio",     description: "Healing frequencies",         category: "calm" },
  { id: "meditation",   label: "Meditation",    description: "Mindful pad",                 category: "calm" },

  // Nature — environmental
  { id: "forest",       label: "Forest",        description: "Dawn birds & leaves",         category: "nature" },
  { id: "rain",         label: "Rain",          description: "Gentle rainfall",             category: "nature" },
  { id: "ocean",        label: "Ocean",         description: "Shoreline waves",             category: "nature" },
  { id: "stream",       label: "Stream",        description: "Flowing water",               category: "nature" },

  // Sleep — deep rest
  { id: "nocturne",     label: "Nocturne",      description: "Soft piano lullaby",          category: "sleep" },
  { id: "healing",      label: "Healing",       description: "Warm acoustic cradle",        category: "sleep" },
  { id: "pink_noise",   label: "Pink Noise",    description: "Consistent deep sleep",       category: "sleep" },
  { id: "deep_drone",   label: "Deep Drone",    description: "Sub-bass stillness",          category: "sleep" },
];

// Performance activities
export const PERFORMANCE_ACTIVITIES = [
  { id: "gym", label: "Gym", icon: Dumbbell, color: "from-red-500 to-orange-500" },
  { id: "golf", label: "Golf", icon: Flag, color: "from-green-500 to-emerald-500" },
  { id: "tennis", label: "Tennis", icon: Trophy, color: "from-yellow-500 to-amber-500" },
  { id: "running", label: "Running", icon: Wind, color: "from-blue-500 to-cyan-500" },
  { id: "martial_arts", label: "Martial Arts", icon: Swords, color: "from-purple-500 to-pink-500" },
  { id: "swimming", label: "Swimming", icon: Sparkles, color: "from-cyan-500 to-blue-500" },
  { id: "general", label: "General", icon: Zap, color: "from-orange-500 to-red-500" },
];

// Performance focus areas
export const PERFORMANCE_FOCUS_AREAS = [
  { id: "confidence", label: "Confidence", icon: Zap },
  { id: "focus", label: "Focus", icon: Target },
  { id: "energy", label: "Energy", icon: Sun },
  { id: "calm", label: "Calm Under Pressure", icon: Heart },
  { id: "sleep", label: "Sleep", icon: Moon },
  { id: "core_confidence", label: "Core Confidence", icon: Shield },
];

// Deep Recovery experiences
export const RECOVERY_EXPERIENCES = [
  { id: "ptsd_support", label: "Gentle PTSD Support", description: "Safe space for processing", icon: Heart, color: "from-rose-400 to-pink-400" },
  { id: "deep_sleep", label: "Deep Sleep Preparation", description: "Calm nervous system activation", icon: Moon, color: "from-indigo-400 to-purple-400" },
  { id: "nervous_system", label: "Nervous System Reset", description: "Restore balance and peace", icon: Leaf, color: "from-emerald-400 to-teal-400" },
  { id: "trauma_release", label: "Trauma Release", description: "Gentle somatic release work", icon: Sparkles, color: "from-violet-400 to-purple-400" },
];

// Atmosphere options for Meditation
export const ATMOSPHERES = [
  { id: "nature", label: "Nature", description: "Forest & ocean sounds" },
  { id: "ambient", label: "Ambient", description: "Soft atmospheric tones" },
];

// Brain state options for advanced settings
export const BRAIN_STATES = [
  { id: "none", label: "None", description: "No binaural beats" },
  { id: "alpha", label: "Alpha", description: "Relaxed awareness" },
  { id: "theta", label: "Theta", description: "Deep meditation" },
  { id: "delta", label: "Delta", description: "Deep sleep" },
];

// Mode configurations
export const MODE_CONFIG = {
  meditation: {
    label: "Calm / Meditation",
    color: "violet",
    gradient: "from-violet-500 to-purple-500",
    bgGradient: "from-violet-500/20 to-purple-500/20",
    textColor: "text-violet-400",
  },
  performance: {
    label: "Performance",
    color: "orange",
    gradient: "from-orange-500 to-red-500",
    bgGradient: "from-orange-500/20 to-red-500/20",
    textColor: "text-orange-400",
  },
  deep_recovery: {
    label: "Deep Recovery",
    color: "slate",
    gradient: "from-slate-500 to-indigo-500",
    bgGradient: "from-slate-500/20 to-indigo-500/20",
    textColor: "text-slate-400",
  },
};

// Animation variants
export const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

export const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Helper function to get mode styling
export const getModeStyles = (mode) => {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.meditation;
  return config;
};

// Helper function to format time
export const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return "∞";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
